// Transactions Service - Firestore Integration
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  getDocs, 
  getDoc,
  query, 
  where,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";
import { db } from "@/lib/firebase";

const COLLECTION_NAME = "transactions";

// Get all transactions for a user
export const getTransactions = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const transactions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Sort by date on client side
    return transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get transactions for a specific month/year
export const getTransactionsByPeriod = async (userId, month, year) => {
  try {
    const startDate = new Date(year, month, 1).toISOString().split('T')[0];
    const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
    
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter and sort on client side
    return all
      .filter(t => t.date >= startDate && t.date <= endDate)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Subscribe to transactions (real-time updates)
export const subscribeToTransactions = (userId, month, year, callback, onError) => {
  const startDate = new Date(year, month, 1).toISOString().split('T')[0];
  const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

  // Filtramos apenas por userId no Firestore e a data no Client-side 
  // para evitar erros de índice ausente e corrigir o looping
 const q = query(
    collection(db, COLLECTION_NAME),
    where("userId", "==", userId),
    where("dueDate", ">=", startDate),
    where("dueDate", "<=", endDate)
  );
  
  return onSnapshot(q, (snapshot) => {
    const transactions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Já vem filtrado do banco, apenas ordenamos
    callback(transactions.sort((a, b) => new Date(b.dueDate || b.date) - new Date(a.dueDate || a.date)));
  }, (error) => {
    // Error silenced for production
    if (onError) onError(error);
  });
};

// Subscribe to transactions in a date range (usado pelo Pente Fino)
export const subscribeToTransactionsRange = (userId, startDate, endDate, callback, onError) => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where("userId", "==", userId),
    where("dueDate", ">=", startDate),
    where("dueDate", "<=", endDate)
  );

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (error) => {
    if (onError) onError(error);
  });
};

// Add a new transaction
export const addTransaction = async (userId, transactionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...transactionData,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...transactionData };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Update a transaction
export const updateTransaction = async (transactionId, transactionData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, transactionId);
    await updateDoc(docRef, {
      ...transactionData,
      updatedAt: serverTimestamp()
    });
    return { id: transactionId, ...transactionData };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Delete a transaction
export const deleteTransaction = async (transactionId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, transactionId));
    return transactionId;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Delete all transactions in a series by seriesId
export const deleteTransactionSeries = async (userId, seriesId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId),
      where("seriesId", "==", seriesId)
    );
    const snapshot = await getDocs(q);
    
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return snapshot.docs.length;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Delete transactions in a series from a specific date onwards
export const deleteSeriesFromDate = async (userId, seriesId, fromDate) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId),
      where("seriesId", "==", seriesId)
    );
    const snapshot = await getDocs(q);
    
    // Filter documents with date >= fromDate
    const docsToDelete = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.date >= fromDate;
    });
    
    const deletePromises = docsToDelete.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return docsToDelete.length;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Update all transactions in a series
export const updateTransactionSeries = async (userId, seriesId, updateData) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId),
      where("seriesId", "==", seriesId)
    );
    const snapshot = await getDocs(q);
    
    const updatePromises = snapshot.docs.map(docSnap => {
      return updateDoc(docSnap.ref, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
    return snapshot.docs.length;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Update transactions in a series from a specific date onwards
export const updateSeriesFromDate = async (userId, seriesId, fromDate, updateData) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId),
      where("seriesId", "==", seriesId)
    );
    const snapshot = await getDocs(q);
    
    // Filter documents with date >= fromDate
    const docsToUpdate = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.date >= fromDate;
    });
    
    const updatePromises = docsToUpdate.map(docSnap => {
      return updateDoc(docSnap.ref, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
    return docsToUpdate.length;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get transaction by ID
export const getTransactionById = async (transactionId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, transactionId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// --- FUNÇÕES DE CHECKLIST (Sincronização entre dispositivos) ---

// Salva se um item foi conferido ou não no banco de dados
export const toggleChecklistItem = async (userId, monthKey, itemId, isChecked) => {
  try {
    // Caminho: checklists -> id_do_usuario -> meses -> 2024-05 (exemplo)
    const docRef = doc(db, "checklists", userId, "months", monthKey);
    
    // O setDoc com { merge: true } atualiza apenas o item específico sem apagar os outros
    await setDoc(docRef, {
      [itemId]: isChecked,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Escuta em tempo real as marcações do checklist
export const subscribeToChecklist = (userId, monthKey, callback) => {
  const docRef = doc(db, "checklists", userId, "months", monthKey);
  
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    } else {
      callback({}); // Se não existir nada marcado, retorna vazio
    }
  });
};

// ---------------------------------------------------------------

export default {
  getTransactions,
  getTransactionsByPeriod,
  subscribeToTransactions,
  addTransaction,
  updateTransaction,
  deleteTransaction,
  deleteTransactionSeries,
  deleteSeriesFromDate,
  updateTransactionSeries,
  updateSeriesFromDate,
  getTransactionById,
  toggleChecklistItem, // Adicionado
  subscribeToChecklist // Adicionado
};
